import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Forms, Help, Loading, SystemText } from '@salesperformanceint/e4shared';
import Filters from '../_filters';
import GroupPicker from 'components/admin/forms/groupPicker';
import WidgetBase, { groupSet, widgetLoad } from '../_base';
import { setDashboardCollection } from 'actions/dashboard';
import { apiGet } from 'services';
import CompletionRate from './components/completionRate';
import ParticipantCount from './components/participantCount';
import ScoreDifferential from './components/scoreDifferential';
import BehaviourTable from './components/behaviourTable';

export const ActionPlanner = ({ defaultProps, id, item, setItem, onDelete }) => {
    const dispatch = useDispatch();
    const showFilters = useSelector(state => state.ui.filterWidget);
    const areas = useSelector(state => state.dashboard.areas);
    const user = useSelector(state => state.user.user);
    const [values, setValues] = React.useState({});
    const [errors, setErrors] = React.useState({});
    const [notConfigured, setNotConfigured] = React.useState(false);
    const [initialised, setInitialised] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const [data, setData] = React.useState([]);
    const [areasMapped, setAreasMapped] = React.useState([]);

    const defaults = { bottom: 5, threshold: 30, contentOnly: false, showDiff: true, period: 'A' };

    const st = (path, ...rest) => SystemText(`Dashboard/Widgets/ActionPlanner/${path}`, ...rest);

    React.useEffect(() => {
        if (!areas?.length) {
            apiGet('competency-area', user.guid, response => dispatch(setDashboardCollection('areas', response)));
        }
    }, []);

    React.useEffect(() => {
        if (item) {
            const parsed = JSON.parse(item.data);
            setValues({ ...defaults, ...parsed });
            setInitialised(true);
        }
    }, [item]);

    React.useEffect(() => {
        if (areas) {
            setAreasMapped(areas.sort((a, b) => (a.name > b.name ? 1 : -1)));
        }
    }, [areas]);

    React.useEffect(() => {
        initialised && load();
    }, [initialised]);

    const load = () => {
        widgetLoad(item.id, user.guid, validator, setLoading, setNotConfigured, setData);
    };

    const updateProperty = (value, prop) => {
        const temp = { ...values };

        if (prop === 'threshold') {
            value = parseInt(value);
        }

        temp[prop] = value;

        setValues(temp);
    };

    const height = typeof defaultProps.height === 'function' ? defaultProps.height(item) : 110 * item.h - 100;

    const validator = suppressErrors => {
        const form = values || JSON.parse(item.data);
        const errors = {};
        let validForm = true;

        if (!form.name) {
            errors.name = SystemText('Dashboard/Widgets/General/Edit/Fields/Name/Error');
            validForm = false;
        }

        if (!groupSet(form.group1)) {
            errors.group1 = st('Fields/Group/Error');
            validForm = false;
        }

        if (!form.area_Ids?.length) {
            errors.area_Ids = st('Fields/Area/Error');
            validForm = false;
        }

        if (!form.threshold) {
            errors.threshold = st('Fields/Threshold/Error');
            validForm = false;
        } else if (form.threshold < 10 || form.threshold > 90) {
            errors.threshold = st('Fields/Threshold/Error');
            validForm = false;
        }

        if (!suppressErrors) setErrors(errors);
        return validForm;
    };

    const consts = {
        rowHeight: 22,
        axisHeight: 20,
        leftMargin: 80,
        padding: 0.2,
    };

    const divHeight = (data, withAxis) => {
        const show = data.length > 20 && !withAxis ? 20 : data.length;
        const height = show * consts.rowHeight + consts.axisHeight;
        if (withAxis) {
            return height + consts.axisHeight;
        }

        return height;
    };

    const hasChartData = React.useMemo(() => {
        return data?.completionRate?.length || data?.participantCount?.length || data?.scoreDifferential?.length;
    }, [data?.completionRate, data?.participantCount, data?.scoreDifferential]);

    const width = values.showDiff ? 4 : 6;
    const chart = (
        <div className="chart-container" id={`chart-${item.id}`} style={{ height: `${height}px` }}>
            <div className="chart" style={{ height: `${height}px` }}>
                {hasChartData > 0 && (
                    <div className="row">
                        <div className={`col-sm-12 col-md-${width} mb5`} id="completion-rate-holder">
                            <div className="mb10">
                                {SystemText('Dashboard/Widgets/ActionPlanner/Charts/Completion/Title')}
                            </div>
                            <CompletionRate
                                data={data.completionRate}
                                consts={{ ...consts, parent: 'completion-rate-holder' }}
                                divHeight={divHeight}
                            />
                        </div>
                        <div className={`col-sm-12 col-md-${width} mb5`} id="participant-holder">
                            <div className="mb10">
                                {SystemText('Dashboard/Widgets/ActionPlanner/Charts/Participant/Title')}
                            </div>
                            <ParticipantCount
                                data={data.participantCount}
                                consts={{ ...consts, parent: 'participant-holder' }}
                                divHeight={divHeight}
                            />
                        </div>
                        {values.showDiff && (
                            <div className={`col-sm-12 col-md-${width} mb5`} id="difference-holder">
                                <div className="mb10">
                                    {SystemText('Dashboard/Widgets/ActionPlanner/Charts/Difference/Title')}
                                    <Help id="diff-chart-help">
                                        {SystemText('Dashboard/Widgets/ActionPlanner/Charts/Difference/Help')}
                                    </Help>
                                </div>
                                <ScoreDifferential
                                    data={data.scoreDifferential}
                                    consts={{ ...consts, parent: 'difference-holder' }}
                                    divHeight={divHeight}
                                />
                            </div>
                        )}
                    </div>
                )}
                <div
                    className={hasChartData > 0 ? 'mt10 pt10' : ''}
                    style={hasChartData ? { borderTop: 'solid 1px rgba(33, 21, 74, 0.10)' } : null}
                >
                    <div className="mb5">{SystemText('Dashboard/Widgets/ActionPlanner/Table/Title')}</div>
                    <div className="mb5" style={{ fontSize: '12px' }}>
                        {SystemText('Dashboard/Widgets/ActionPlanner/Table/Help')}
                    </div>
                    <BehaviourTable data={data.behaviours} widget={item.id} />
                </div>
            </div>
        </div>
    );

    const filters = (
        <Filters
            validator={validator}
            widget={item}
            values={values}
            errors={errors}
            onChange={(value, prop) => updateProperty(value, prop)}
            refresh={x => {
                setItem(x);
                load();
            }}
            show={showFilters === id}
            onDelete={onDelete}
        >
            <GroupPicker
                help={st('Fields/Group/Help')}
                mandatory
                allowMultiple
                value={values.group1}
                error={errors.group1}
                onChange={value => updateProperty(value, 'group1')}
                showUserOption={defaultProps.company}
                controlId={id}
            />
            <Forms.Select
                label={st('Fields/Area/Label')}
                help={st('Fields/Area/Help')}
                mandatory
                allowMultiple
                data={areasMapped}
                value={values.area_Ids}
                error={errors.area_Ids}
                onChange={value => updateProperty(value, 'area_Ids')}
                id={`action-planner-area-ids-${id}`}
            />
            <div className="mtm10 mb10">
                {(!values.area_Ids?.length || values.area_Ids?.length < areasMapped.length) && (
                    <button
                        className="btn btn-link"
                        onClick={() => {
                            setValues({ ...values, area_Ids: areasMapped.map(x => x.id) });
                        }}
                    >
                        {st('Fields/Area/SelectAll')}
                    </button>
                )}
                {values.area_Ids?.length === areasMapped.length && (
                    <button
                        className="btn btn-link"
                        onClick={() => {
                            setValues({ ...values, area_Ids: [] });
                        }}
                    >
                        {st('Fields/Area/ClearAll')}
                    </button>
                )}
            </div>
            <Forms.Select
                label={st('Fields/Bottom/Label')}
                help={st('Fields/Bottom/Help')}
                mandatory
                data={[
                    { id: 3, name: '3' },
                    { id: 5, name: '5' },
                    { id: 12, name: '12' },
                ]}
                value={values.bottom}
                error={errors.bottom}
                onChange={value => updateProperty(value, 'bottom')}
            />
            <Forms.Text
                label={st('Fields/Threshold/Label')}
                help={st('Fields/Threshold/Help')}
                mandatory
                type="number"
                maxLength="2"
                value={values.threshold}
                error={errors.threshold}
                onChange={value => updateProperty(value, 'threshold')}
            />
            <Forms.Toggle
                label={st('Fields/ContentOnly/Label')}
                help={st('Fields/ContentOnly/Help')}
                value={values.contentOnly}
                error={errors.contentOnly}
                onChange={value => updateProperty(value, 'contentOnly')}
            />
            <Forms.Toggle
                label={st('Fields/ShowDiff/Label')}
                help={st('Fields/ShowDiff/Help')}
                value={values.showDiff}
                error={errors.showDiff}
                onChange={value => updateProperty(value, 'showDiff')}
            />
            <Forms.Select
                label={st('Fields/Period/Label')}
                help={st('Fields/Period/Help')}
                mandatory
                data={[
                    { id: 'A', name: 'All Time' },
                    { id: 'W', name: 'Week' },
                    { id: 'M', name: 'Month' },
                    { id: 'Q', name: 'Quarter' },
                    { id: 'Y', name: 'Financial Year' },
                ]}
                value={values.period}
                error={errors.period}
                onChange={value => updateProperty(value, 'period')}
            />
        </Filters>
    );

    return (
        <WidgetBase
            filters={filters}
            chart={chart}
            height={height}
            item={item}
            recommendations={null}
            id={id}
            help={null}
            hideConfig={defaultProps.company && !user.administrator}
            extra={null}
            loading={loading}
            notConfigured={notConfigured}
        />
    );
};

export default ActionPlanner;
